import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Capacitor } from '@capacitor/core';
import { useNavigate } from 'react-router-dom';
import { UnifiedHeader } from './UnifiedHeader';
import BottomSheet from './BottomSheet';
import AuctionSetupModal, { AuctionEditData } from './AuctionSetupModal';
import { InventoryContent } from './InventoryContent';
import { UnifiedItemEditModal } from './itemEdit';
import { EditableItem } from './itemEdit/types';
import ImageAttributionModal from './ImageAttributionModal';
import { ItemGrid } from './inventory/ItemGrid';
import { MarketplaceItemCard } from './marketplace/MarketplaceItemCard';
import { ContextHelpTip } from './ContextHelpTip';
import ContactSellerModal from './ContactSellerModal';
import { useAuth } from '../contexts/AuthContext';
import { sizeToLayout, ThumbnailSize } from '../types/viewComposition';
import API_BASE_URL, { API_ENDPOINTS } from '../config';
import { useMode, ModeType } from '../contexts/ModeContext';
import { useSpace } from '../contexts/SpaceContext';
import '../styles/pages/guestCollectionView.css';
import '../styles/pages/auctionDetailPage.css';
import '../styles/components/itemGrid.css';
import '../styles/components/itemCard.css';

type Filter = 'all' | 'my-auctions' | 'my-bids' | 'my-depots';

interface MarketTabProps {
  isActive: boolean;
  onFontSizeToggle?: () => void;
  fontSize?: string;
  activeMode: ModeType;
  onOpenAppContext: () => void;
}

/* ---------- shared helpers ---------- */
const centsToDisplay = (cents: number) => (cents / 100).toFixed(2);
const formatDate = (iso: string) => {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};
const formatTimeRemaining = (endsAt: string): string => {
  const now = new Date();
  const end = new Date(endsAt);
  const diff = end.getTime() - now.getTime();
  if (diff <= 0) return 'Ended';
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
};

/* ---------- filter labels ---------- */
const FILTER_LABELS: Record<Filter, string> = {
  'all': 'All',
  'my-auctions': 'My Auctions',
  'my-depots': 'My Depots',
  'my-bids': 'My Bids',
};

/* ---------- condition labels ---------- */
const CONDITION_LABELS: Record<string, string> = {
  new: 'New', like_new: 'Like New', good: 'Good', fair: 'Fair', poor: 'Poor',
};

/* ---------- thumbnail size map ---------- */
const THUMB_PX: Record<string, number> = { small: 96, medium: 110, large: 140, 'x-large': 160 };
const THUMB_FONT: Record<string, string> = { small: '20px', medium: '24px', large: '28px', 'x-large': '32px' };

/* ---------- card styles ---------- */
const cardStyle: React.CSSProperties = {
  background: 'var(--color-bg-primary)',
  borderBottom: '1px solid var(--color-border)',
  padding: '12px 16px',
};

const formatDistance = (miles: number | null): string | null => {
  if (miles === null) return null;
  if (miles < 1) return '<1 mi away';
  return `~${Math.round(miles)} mi away`;
};

const getUserIdFromToken = (token: string | null): number | null => {
  if (!token) return null;
  try { return JSON.parse(atob(token.split('.')[1])).user_id; } catch { return null; }
};

const MarketTab: React.FC<MarketTabProps> = ({ 
  isActive, 
  onFontSizeToggle, 
  fontSize, 
  activeMode, 
  onOpenAppContext 
}) => {
  const navigate = useNavigate();
  const token = localStorage.getItem('auth_token');
  const { user } = useAuth();
  const { activeSpace } = useSpace();

  /* Read thumbnail size preference from Display settings (same key as Inventory) */
  const [thumbnailSize, setThumbnailSize] = useState<ThumbnailSize>(() => {
    const saved = localStorage.getItem('inventory-thumbnailSize');
    return (saved as ThumbnailSize) || 'medium';
  });

  // Listen for thumbnail size changes from ViewSettingsModal / InventoryView
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'inventory-thumbnailSize' && e.newValue) {
        setThumbnailSize(e.newValue as ThumbnailSize);
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const savedThumbSize = thumbnailSize;
  const thumbPx = THUMB_PX[savedThumbSize] || 110;
  const thumbStyle: React.CSSProperties = {
    width: thumbPx, height: thumbPx, borderRadius: 8,
    objectFit: 'cover', background: 'var(--color-bg-tertiary)', flexShrink: 0,
  };
  const thumbPlaceholder: React.CSSProperties = {
    ...thumbStyle, display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: THUMB_FONT[savedThumbSize] || '24px', fontWeight: 700, color: 'var(--color-text-muted)',
  };
  const currentUserId = getUserIdFromToken(token);
  const [filter, setFilter] = useState<Filter>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [imageModalItem, setImageModalItem] = useState<{ image_id: number; name: string; isMine: boolean; itemId: number; isDepot: boolean; depotId?: number } | null>(null);
  const [expandedDescs, setExpandedDescs] = useState<Set<string>>(new Set());
  const filterDropdownRef = useRef<HTMLDivElement>(null);

  /* ============= PROXIMITY SEARCH ============= */
  const DISTANCE_PRESETS = [5, 10, 25, 50, 100];
  const [viewerLoc, setViewerLoc] = useState<{ lat: number; lng: number } | null>(() => {
    const saved = localStorage.getItem('marketplace-viewer-loc');
    if (saved) { try { return JSON.parse(saved); } catch { return null; } }
    return null;
  });

  // Sync viewerLoc from user profile when available (no localStorage override)
  useEffect(() => {
    if (!viewerLoc && user?.latitude && user?.longitude) {
      const loc = { lat: user.latitude, lng: user.longitude };
      saveViewerLoc(loc);
    }
  }, [user?.latitude, user?.longitude]);
  const [maxDistance, setMaxDistance] = useState<number | null>(() => {
    const saved = localStorage.getItem('marketplace-max-distance');
    return saved ? Number(saved) : null;
  });
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [locationError, setLocationError] = useState<string | null>(null);
  const [locatingBrowser, setLocatingBrowser] = useState(false);
  const [customDistanceInput, setCustomDistanceInput] = useState('');

  const saveViewerLoc = (loc: { lat: number; lng: number }) => {
    setViewerLoc(loc);
    localStorage.setItem('marketplace-viewer-loc', JSON.stringify(loc));
    setLocationError(null);
  };

  const handleBrowserLocation = () => {
    if (!navigator.geolocation) { setLocationError('Geolocation not supported.'); return; }
    setLocatingBrowser(true);
    setLocationError(null);
    // Manual timeout guard — some mobile browsers never fire the error callback
    const fallbackTimer = setTimeout(() => {
      setLocatingBrowser(false);
      setLocationError('Location request timed out. Try entering a zip code instead.');
    }, 15000);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        clearTimeout(fallbackTimer);
        saveViewerLoc({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setLocatingBrowser(false);
        setCustomDistanceInput('');
      },
      (err) => {
        clearTimeout(fallbackTimer);
        setLocatingBrowser(false);
        setLocationError(err.code === 1 ? 'Location access denied. Try a zip code.' : 'Could not determine location. Try a zip code.');
      },
      { enableHighAccuracy: false, timeout: 10000 }
    );
  };

  const handleZipLookup = async (zip: string) => {
    if (!/^\d{5}$/.test(zip.trim())) { setLocationError('Enter a valid 5-digit zip code.'); return; }
    setLocationError(null);
    setLocatingBrowser(true);
    try {
      const res = await fetch(`https://api.zippopotam.us/us/${zip.trim()}`);
      if (!res.ok) { setLocationError('Zip not found.'); setLocatingBrowser(false); return; }
      const data = await res.json();
      const place = data.places?.[0];
      if (!place) { setLocationError('Zip not found.'); setLocatingBrowser(false); return; }
      saveViewerLoc({ lat: parseFloat(place.latitude), lng: parseFloat(place.longitude) });
      setLocatingBrowser(false);
      setCustomDistanceInput('');
    } catch { setLocationError('Zip lookup failed.'); setLocatingBrowser(false); }
  };

  const handleProximityButtonClick = () => {
    if (!viewerLoc) { setShowLocationModal(true); }
    else if (maxDistance) { setMaxDistance(null); localStorage.removeItem('marketplace-max-distance'); }
    else { setShowLocationModal(true); }
  };

  const selectDistance = (miles: number) => {
    setMaxDistance(miles);
    localStorage.setItem('marketplace-max-distance', String(miles));
    setShowLocationModal(false);
    // Re-fetch so backend computes distances with the viewer coords
    fetchBrowse();
    fetchAllDepotItems();
  };

  const clearProximity = () => {
    setMaxDistance(null);
    setViewerLoc(null);
    localStorage.removeItem('marketplace-max-distance');
    localStorage.removeItem('marketplace-viewer-loc');
  };

  const getItemMiles = (item: any): number | null => {
    if (item._type === 'auction') return item.distance_miles ?? null;
    if (item._type === 'depot' && item.distance?.miles != null) return item.distance.miles;
    return null;
  };

  /* ============= VOICE SEARCH ============= */
  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef<any>(null);

  useEffect(() => {
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechRecognition) return;
    const recognition = new SpeechRecognition();
    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.lang = 'en-US';
    recognition.onresult = (event: any) => {
      let transcript = '';
      for (let i = 0; i < event.results.length; i++) {
        transcript += event.results[i][0].transcript;
      }
      setSearchTerm(transcript.trim());
    };
    recognition.onstart = () => setIsListening(true);
    recognition.onend = () => setIsListening(false);
    recognition.onerror = () => setIsListening(false);
    recognitionRef.current = recognition;
    return () => { try { recognition.stop(); } catch {} };
  }, []);

  const toggleVoice = useCallback(() => {
    if (!recognitionRef.current) return;
    if (isListening) {
      try { recognitionRef.current.stop(); } catch {}
    } else {
      setSearchTerm('');
      try { recognitionRef.current.start(); } catch {}
    }
  }, [isListening]);

  /* Close filter dropdown on outside click */
  useEffect(() => {
    if (!showFilterDropdown) return;
    const handleClick = (e: MouseEvent) => {
      if (filterDropdownRef.current && !filterDropdownRef.current.contains(e.target as Node)) {
        setShowFilterDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showFilterDropdown]);

  /* ============= AUCTION DETAIL BOTTOM SHEET ============= */
  const [detailAuctionId, setDetailAuctionId] = useState<number | null>(null);
  const [detailAuction, setDetailAuction] = useState<any>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [bidAmount, setBidAmount] = useState('');
  const [bidComment, setBidComment] = useState('');
  const [bidSubmitting, setBidSubmitting] = useState(false);
  const [bidInputFocused, setBidInputFocused] = useState(false);
  const [bidKeyboardHeight, setBidKeyboardHeight] = useState(0);
  const bidInputRef = useRef<HTMLInputElement>(null);
  const [bidMessage, setBidMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [detailTimeRemaining, setDetailTimeRemaining] = useState('');
  // Seller actions
  const [sellerActionLoading, setSellerActionLoading] = useState(false);
  const [rankings, setRankings] = useState<any[]>([]);
  const [rankingsLoading, setRankingsLoading] = useState(false);
  const [editAuctionData, setEditAuctionData] = useState<any>(null);

  const openAuctionDetail = (auctionId: number) => {
    setDetailAuctionId(auctionId);
    setDetailAuction(null);
    setDetailError(null);
    setBidMessage(null);
  };

  const closeAuctionDetail = () => {
    setDetailAuctionId(null);
    setDetailAuction(null);
    setDetailError(null);
    setBidAmount('');
    setBidComment('');
    setBidMessage(null);
    setRankings([]);
    setBidKeyboardHeight(0);
    setBidInputFocused(false);
  };

  // Track keyboard height when bid input is focused
  useEffect(() => {
    if (!bidInputFocused) { setBidKeyboardHeight(0); return; }
    const isIOSNative = Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios';
    if (isIOSNative) {
      const { Keyboard } = require('@capacitor/keyboard');
      const showListener = Keyboard.addListener('keyboardWillShow', (info: any) => {
        setBidKeyboardHeight(info.keyboardHeight || 300);
      });
      const hideListener = Keyboard.addListener('keyboardWillHide', () => {
        setBidKeyboardHeight(0);
      });
      return () => {
        showListener.then((l: any) => l.remove());
        hideListener.then((l: any) => l.remove());
      };
    } else {
      const vv = window.visualViewport;
      if (!vv) return;
      const onResize = () => {
        const offset = window.innerHeight - vv.height;
        setBidKeyboardHeight(offset > 50 ? offset : 0);
      };
      vv.addEventListener('resize', onResize);
      vv.addEventListener('scroll', onResize);
      return () => {
        vv.removeEventListener('resize', onResize);
        vv.removeEventListener('scroll', onResize);
      };
    }
  }, [bidInputFocused]);

  const fetchAuctionDetail = useCallback(async () => {
    if (!detailAuctionId || !token) return;
    setDetailLoading(true);
    setDetailError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/auction-items/browse/${detailAuctionId}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to load auction');
      const data = await res.json();
      setDetailAuction(data);
      if (data.my_bid) {
        setBidAmount((data.my_bid.amount / 100).toFixed(2));
        setBidComment(data.my_bid.comment || '');
      } else {
        setBidAmount((data.minimum_bid / 100).toFixed(2));
        setBidComment('');
      }
    } catch {
      setDetailError('Unable to load auction.');
    } finally {
      setDetailLoading(false);
    }
  }, [detailAuctionId, token]);

  useEffect(() => { fetchAuctionDetail(); }, [fetchAuctionDetail]);

  useEffect(() => {
    if (!detailAuction) return;
    const update = () => setDetailTimeRemaining(formatTimeRemaining(detailAuction.ends_at));
    update();
    const interval = setInterval(update, 30000);
    return () => clearInterval(interval);
  }, [detailAuction]);

  const handlePlaceBid = async () => {
    if (!detailAuction || !token) return;
    const amountCents = Math.round(parseFloat(bidAmount) * 100);
    if (isNaN(amountCents) || amountCents < detailAuction.minimum_bid) {
      setBidMessage({ type: 'error', text: `Bid must be at least $${centsToDisplay(detailAuction.minimum_bid)}` });
      return;
    }
    if (bidComment.length > 500) {
      setBidMessage({ type: 'error', text: 'Comment must be 500 characters or fewer' });
      return;
    }
    setBidSubmitting(true);
    setBidMessage(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/auction-items/${detailAuction.id}/bid`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ amount: amountCents, comment: bidComment.trim() || null }),
      });
      const data = await res.json();
      if (!res.ok) { setBidMessage({ type: 'error', text: data.error || 'Failed to place bid' }); return; }
      setBidMessage({ type: 'success', text: `Bid of $${centsToDisplay(amountCents)} placed.` });
      fetchAuctionDetail();
    } catch { setBidMessage({ type: 'error', text: 'Failed to place bid.' }); }
    finally { setBidSubmitting(false); }
  };

  const handleWithdrawBid = async () => {
    if (!detailAuction || !token) return;
    if (!window.confirm('Withdraw your bid?')) return;
    setBidSubmitting(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/auction-items/${detailAuction.id}/bid`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) { const d = await res.json(); setBidMessage({ type: 'error', text: d.error || 'Failed to withdraw bid' }); return; }
      setBidMessage({ type: 'success', text: 'Bid withdrawn.' });
      setBidAmount(''); setBidComment('');
      fetchAuctionDetail();
    } catch { setBidMessage({ type: 'error', text: 'Failed to withdraw bid.' }); }
    finally { setBidSubmitting(false); }
  };

  const handleBuyNow = async () => {
    if (!detailAuction || !token) return;
    if (!window.confirm(`Express interest in buying at $${centsToDisplay(detailAuction.buy_now_price)}?`)) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/auction-items/${detailAuction.id}/buy-now`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) { setBidMessage({ type: 'error', text: data.error || 'Failed' }); return; }
      setBidMessage({ type: 'success', text: 'Seller has been notified of your interest.' });
    } catch { setBidMessage({ type: 'error', text: 'Failed to send request.' }); }
  };

  /* ============= SELLER ACTIONS ============= */
  const fetchRankings = useCallback(async (auctionId: number) => {
    if (!token) return;
    setRankingsLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/auction-items/${auctionId}/rankings`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) { const data = await res.json(); setRankings(data.rankings || []); }
      else setRankings([]);
    } catch { setRankings([]); }
    finally { setRankingsLoading(false); }
  }, [token]);

  // Auto-fetch rankings when seller views a closed/completed auction
  useEffect(() => {
    if (detailAuction?.is_seller && (detailAuction.status === 'closed' || detailAuction.status === 'completed')) {
      fetchRankings(detailAuction.id);
    }
  }, [detailAuction, fetchRankings]);

  const handleSellerClose = async () => {
    if (!detailAuction || !token) return;
    if (!window.confirm('Close this auction early? Bids will be ranked and a winner selected if reserve is met.')) return;
    setSellerActionLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/auction-items/${detailAuction.id}/close`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) { const d = await res.json(); setBidMessage({ type: 'error', text: d.error || 'Failed' }); return; }
      setBidMessage({ type: 'success', text: 'Auction closed.' });
      fetchAuctionDetail(); fetchMyAuctions();
    } catch { setBidMessage({ type: 'error', text: 'Failed to close auction.' }); }
    finally { setSellerActionLoading(false); }
  };

  const handleSellerWithdraw = async () => {
    if (!detailAuction || !token) return;
    if (!window.confirm('Withdraw this auction? It will be cancelled.')) return;
    setSellerActionLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/auction-items/${detailAuction.id}`, {
        method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) { const d = await res.json(); setBidMessage({ type: 'error', text: d.error || 'Failed' }); return; }
      setBidMessage({ type: 'success', text: 'Auction withdrawn.' });
      closeAuctionDetail(); fetchMyAuctions();
    } catch { setBidMessage({ type: 'error', text: 'Failed to withdraw.' }); }
    finally { setSellerActionLoading(false); }
  };

  const handleSellerPublish = async () => {
    if (!detailAuction || !token) return;
    setSellerActionLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/auction-items/${detailAuction.id}/publish`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) { const d = await res.json(); setBidMessage({ type: 'error', text: d.error || 'Failed' }); return; }
      setBidMessage({ type: 'success', text: 'Auction published!' });
      fetchAuctionDetail(); fetchMyAuctions();
    } catch { setBidMessage({ type: 'error', text: 'Failed to publish.' }); }
    finally { setSellerActionLoading(false); }
  };

  const handleSellerCollect = async () => {
    if (!detailAuction || !token) return;
    setSellerActionLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/auction-items/${detailAuction.id}/collect`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) { const d = await res.json(); setBidMessage({ type: 'error', text: d.error || 'Failed' }); return; }
      setBidMessage({ type: 'success', text: 'Item marked as collected!' });
      fetchAuctionDetail(); fetchMyAuctions();
    } catch { setBidMessage({ type: 'error', text: 'Failed.' }); }
    finally { setSellerActionLoading(false); }
  };

  const handleSellerForfeit = async () => {
    if (!detailAuction || !token) return;
    if (!window.confirm('Mark the winner as forfeited? The next runner-up will be promoted.')) return;
    setSellerActionLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/auction-items/${detailAuction.id}/forfeit`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) { const d = await res.json(); setBidMessage({ type: 'error', text: d.error || 'Failed' }); return; }
      setBidMessage({ type: 'success', text: 'Winner forfeited. Next runner-up promoted.' });
      fetchAuctionDetail(); fetchMyAuctions();
    } catch { setBidMessage({ type: 'error', text: 'Failed.' }); }
    finally { setSellerActionLoading(false); }
  };

  const sellerActionBtnStyle = (color: string): React.CSSProperties => ({
    padding: '10px 16px', fontSize: 14, fontWeight: 600, borderRadius: 8,
    border: 'none', background: color, color: '#fff', cursor: 'pointer',
    opacity: sellerActionLoading ? 0.6 : 1,
  });

  /* ============= BROWSE STATE ============= */
  const [browseItems, setBrowseItems] = useState<any[]>([]);
  const [browseLoading, setBrowseLoading] = useState(false);

  const fetchBrowse = useCallback(async () => {
    if (!token) return;
    setBrowseLoading(true);
    try {
      const savedLoc = localStorage.getItem('marketplace-viewer-loc');
      const loc = savedLoc ? JSON.parse(savedLoc) : null;
      const coordQ = loc ? `&lat=${loc.lat}&lng=${loc.lng}` : '';
      const res = await fetch(`${API_BASE_URL}/api/auction-items/browse?sort=ending_soon${coordQ}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setBrowseItems(Array.isArray(data) ? data : []);
      }
    } catch { /* ignore */ } finally {
      setBrowseLoading(false);
    }
  }, [token]);

  /* ============= MY AUCTIONS STATE ============= */
  const [myAuctions, setMyAuctions] = useState<any[]>([]);
  const [auctionsLoading, setAuctionsLoading] = useState(false);

  const fetchMyAuctions = useCallback(async () => {
    if (!token) return;
    setAuctionsLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/auction-items/mine`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setMyAuctions(Array.isArray(data) ? data : []);
      }
    } catch { /* ignore */ } finally {
      setAuctionsLoading(false);
    }
  }, [token]);

  /* ============= ALL DEPOT ITEMS (for unified All view) ============= */
  const [allDepotItems, setAllDepotItems] = useState<any[]>([]);
  const [allDepotItemsLoading, setAllDepotItemsLoading] = useState(false);

  const fetchAllDepotItems = useCallback(async () => {
    if (!token) return;
    setAllDepotItemsLoading(true);
    try {
      const savedLoc = localStorage.getItem('marketplace-viewer-loc');
      const loc = savedLoc ? JSON.parse(savedLoc) : null;
      const coordQ = loc ? `?lat=${loc.lat}&lng=${loc.lng}` : '';
      const res = await fetch(`${API_BASE_URL}/api/collections/marketplace/public/items${coordQ}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setAllDepotItems(data.items || []);
      }
    } catch { /* ignore */ } finally {
      setAllDepotItemsLoading(false);
    }
  }, [token]);

  /* ============= MY DEPOTS STATE ============= */
  const [myDepots, setMyDepots] = useState<any[]>([]);
  const [depotsLoading, setDepotsLoading] = useState(false);

  /* ============= DEPOT DETAIL (INLINE DRILL-DOWN) ============= */
  const [selectedDepot, setSelectedDepot] = useState<any>(null);
  const [depotItems, setDepotItems] = useState<any[]>([]);
  const [depotItemsLoading, setDepotItemsLoading] = useState(false);

  const openDepotDetail = useCallback(async (depot: any) => {
    setSelectedDepot(depot);
    setDepotItems([]);
    setDepotItemsLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/collections/marketplace/public/${depot.depot_id}`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      });
      if (res.ok) {
        const data = await res.json();
        setDepotItems(data.items || []);
        // Enrich selectedDepot with full collection metadata
        if (data.collection) {
          setSelectedDepot((prev: any) => ({ ...prev, ...data.collection, depot_id: prev.depot_id }));
        }
      }
    } catch { /* ignore */ } finally {
      setDepotItemsLoading(false);
    }
  }, [token]);

  const fetchMyDepots = useCallback(async () => {
    if (!token) return;
    setDepotsLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/collections/marketplace/public`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        const allDepots: any[] = data.depots || [];
        setMyDepots(currentUserId ? allDepots.filter((d: any) => d.owner_id === currentUserId) : []);
      }
    } catch { /* ignore */ } finally {
      setDepotsLoading(false);
    }
  }, [token, currentUserId]);

  /* ============= MY BIDS STATE ============= */
  const [myBids, setMyBids] = useState<any[]>([]);
  const [bidsLoading, setBidsLoading] = useState(false);

  const fetchMyBids = useCallback(async () => {
    if (!token) return;
    setBidsLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/auction-items/bids/mine`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setMyBids(Array.isArray(data) ? data : []);
      }
    } catch { /* ignore */ } finally {
      setBidsLoading(false);
    }
  }, [token]);

  /* Fetch all sources on mount / when tab becomes active */
  useEffect(() => {
    if (!isActive) return;
    fetchBrowse();
    fetchMyAuctions();
    fetchMyBids();
    fetchMyDepots();
    fetchAllDepotItems();
  }, [isActive, fetchBrowse, fetchMyAuctions, fetchMyBids, fetchMyDepots, fetchAllDepotItems]);

  /* Build unified items list: auctions + depot items, with type flags */
  const myAuctionIds = new Set(myAuctions.map((a: any) => a.id));

  // Build a map of item_id → depot item for cross-referencing
  const depotByItemId = new Map<number, any>();
  for (const d of allDepotItems) depotByItemId.set(d.id, d);

  // Only non-terminal auctions should appear in the unified browse view
  const activeMyAuctions = myAuctions.filter((a: any) => !['cancelled', 'completed'].includes(a.status));

  // Set of item_ids that have an active auction — used to deduplicate depot items
  const now = Date.now();
  const isLiveAuction = (a: any) => !a.ends_at || new Date(a.ends_at).getTime() > now;

  const allAuctions = [
    ...browseItems,
    ...activeMyAuctions.filter((a: any) => !browseItems.some((b: any) => b.id === a.id)),
  ];
  const auctionedItemIds = new Set(allAuctions.filter(isLiveAuction).map((a: any) => a.item_id));

  const unifiedItems: any[] = [
    ...browseItems.filter(isLiveAuction).map((item: any) => {
      const base: any = { ...item, _source: 'browse' as const, _type: 'auction' as const, _isMine: myAuctionIds.has(item.id) };
      const depot = depotByItemId.get(item.item_id);
      if (depot && item.buy_now_price != null) {
        base._depotItem = depot;
        base._hasBuyNow = true;
      }
      return base;
    }),
    ...activeMyAuctions
      .filter((a: any) => !browseItems.some((b: any) => b.id === a.id) && isLiveAuction(a))
      .map((a: any) => {
        const base: any = { ...a, _source: 'mine' as const, _type: 'auction' as const, _isMine: true };
        const depot = depotByItemId.get(a.item_id);
        if (depot && a.buy_now_price != null) {
          base._depotItem = depot;
          base._hasBuyNow = true;
        }
        return base;
      }),
    // Filter out depot items that already appear as auction items
    ...allDepotItems
      .filter((item: any) => !auctionedItemIds.has(item.id))
      .map((item: any) => ({
        ...item,
        _source: 'depot' as const,
        _type: 'depot' as const,
        _isMine: item.owner_id === currentUserId,
      })),
  ];

  const searchLower = searchTerm.toLowerCase().trim();
  const matchesSearch = (text: string | null | undefined) => !searchLower || (text || '').toLowerCase().includes(searchLower);

  const filteredItems = (filter === 'all'
    ? unifiedItems
    : filter === 'my-auctions'
      ? myAuctions.map((a: any) => {
          const base: any = { ...a, _source: 'mine' as const, _type: 'auction' as const, _isMine: true };
          const depot = depotByItemId.get(a.item_id);
          if (depot && a.buy_now_price != null) { base._depotItem = depot; base._hasBuyNow = true; }
          return base;
        })
      : [] // my-bids / my-depots handled separately
  ).filter((item) => matchesSearch(item.item_name) || matchesSearch(item.name) || matchesSearch(item.auction_group) || matchesSearch(item.depot_name))
    .filter((item) => {
      if (!maxDistance || !viewerLoc) return true;
      const miles = getItemMiles(item);
      if (miles === null) return item._isMine; // keep own items (backend skips self-distance)
      return miles <= maxDistance;
    });

  const filteredBids = myBids.filter((bid: any) => matchesSearch(bid.item_name));
  const filteredDepots = myDepots.filter((d: any) => matchesSearch(d.name) || matchesSearch(d.description));

  /* ============= RENDERERS ============= */
  const isLoading = browseLoading || auctionsLoading || allDepotItemsLoading;

  const statusBadge = (item: any) => {
    const status = item.status || 'active';
    const isActive = status === 'active';
    const isClosed = status === 'closed' || status === 'completed';
    return {
      label: isActive ? 'Active' : status === 'closed' ? (item.outcome === 'sold' ? 'Sold' : 'Unsold') : status === 'completed' ? 'Completed' : status.charAt(0).toUpperCase() + status.slice(1),
      bg: isActive ? '#dcfce7' : isClosed ? '#f3f4f6' : '#dbeafe',
      color: isActive ? '#166534' : isClosed ? '#374151' : '#1e40af',
    };
  };

  const thumbSize = thumbnailSize;
  const thumbLayout = sizeToLayout(thumbSize);

  const handleShareClick = (item: any, e: React.MouseEvent) => {
    const itemName = item.item_name || item.name;
    const shareUrl = `${API_BASE_URL}/api/auction-items/${item.id}/share`;
    if (navigator.share) {
      navigator.share({ title: itemName, url: shareUrl }).catch(() => {});
    } else {
      navigator.clipboard.writeText(shareUrl).then(() => {
        const btn = e.currentTarget as HTMLElement;
        btn.style.color = 'var(--color-success, #16a34a)';
        setTimeout(() => { btn.style.color = ''; }, 1200);
      }).catch(() => {});
    }
  };

  const handleNameClick = (item: any) => {
    const isDepot = item._type === 'depot';
    const itemName = isDepot ? item.name : (item.item_name || item.name);
    const itemId = isDepot ? item.id : (item.item_id || item.id);
    const condition = isDepot ? item.condition : (item.item_condition || item.condition);
    const description = isDepot ? item.description : (item.item_description || item.description);
    const isMine = item._isMine || (!isDepot && item.seller_id === currentUserId);
    // For auction items, cross-reference depot data for fields the auction API doesn't return
    const depot = !isDepot ? depotByItemId.get(item.item_id) : null;
    if (isMine) {
      setEditingDepotItem({
        id: itemId,
        name: itemName || '',
        description: description || '',
        color: '',
        area: '',
        location: '',
        quantity: item.quantity ?? depot?.quantity ?? 1,
        quantity_min: item.quantity_min ?? depot?.quantity_min,
        purchase_price: item.purchase_price ?? depot?.purchase_price,
        sale_price: item.sale_price ?? depot?.sale_price,
        condition: condition,
        image_id: item.image_id,
      } as any);
    }
  };

  const handleImageClick = (item: any) => {
    const isDepot = item._type === 'depot';
    const itemName = isDepot ? item.name : (item.item_name || item.name);
    const itemId = isDepot ? item.id : (item.item_id || item.id);
    const isMine = item._isMine || (!isDepot && item.seller_id === currentUserId);
    if (item.image_id) {
      setImageModalItem({ image_id: item.image_id, name: itemName, isMine, itemId, isDepot, depotId: isDepot ? item.depot_id : undefined });
    }
  };

  const handleDepotClick = (item: any) => {
    openDepotDetail({ depot_id: item.depot_id, name: item.depot_name, owner_name: item.owner_name, owner_id: item.owner_id });
  };

  const handleItemClick = (item: any) => {
    if (item._type !== 'depot') {
      openAuctionDetail(item.id);
    }
  };

  const renderItems = () => {
    if (isLoading && filteredItems.length === 0) return <div style={{ padding: 32, textAlign: 'center', color: 'var(--color-text-muted)' }}>Loading...</div>;
    if (filteredItems.length === 0) {
      if (searchLower) return <div style={{ padding: 32, textAlign: 'center', color: 'var(--color-text-muted)' }}>No matching items.</div>;
      if (filter === 'my-auctions') return (
        <div style={{ padding: 32, textAlign: 'center', color: 'var(--color-text-muted)' }}>
          <div style={{ marginBottom: 8 }}>No auctions yet.</div>
          <div style={{ fontSize: '13px' }}>Create one from any item's context menu in Inventory.</div>
        </div>
      );
      return <div style={{ padding: 32, textAlign: 'center', color: 'var(--color-text-muted)' }}>No active listings right now.</div>;
    }
    return (
      <ItemGrid layout={thumbLayout} size={thumbSize}>
        {filteredItems.map((item: any) => {
          const isDepot = item._type === 'depot';
          const cardKey = isDepot ? `depot-${item.depot_id}-${item.id}` : `${item._source}-${item.id}`;
          const isMine = item._isMine || (!isDepot && item.seller_id === currentUserId);
          return (
            <MarketplaceItemCard
              key={cardKey}
              item={item}
              size={thumbSize}
              currentUserId={currentUserId}
              isSelected={!isMine && marketSelectedKeys.has(cardKey)}
              onClick={isMine ? handleItemClick : (itm: any) => {
                toggleMarketItemSelect(itm);
              }}
              onImageClick={isMine ? handleImageClick : (itm: any) => {
                toggleMarketItemSelect(itm);
              }}
              onNameClick={isMine ? handleNameClick : undefined}
              onDepotClick={handleDepotClick}
              onShareClick={!isDepot ? handleShareClick : undefined}
              onBidClick={!isDepot && !isMine ? (itm: any, e: React.MouseEvent) => {
                e.stopPropagation();
                openAuctionDetail(itm.id);
              } : undefined}
              expandedDescs={expandedDescs}
              onToggleDesc={(key) => setExpandedDescs(prev => {
                const next = new Set(prev);
                if (next.has(key)) next.delete(key); else next.add(key);
                return next;
              })}
            />
          );
        })}
      </ItemGrid>
    );
  };

  const renderBids = () => {
    if (bidsLoading) return <div style={{ padding: 32, textAlign: 'center', color: 'var(--color-text-muted)' }}>Loading...</div>;
    if (filteredBids.length === 0) return <div style={{ padding: 32, textAlign: 'center', color: 'var(--color-text-muted)' }}>{searchLower ? 'No matching bids.' : "You haven't placed any bids yet."}</div>;
    const resultColors: Record<string, { bg: string; color: string }> = {
      winner: { bg: '#dcfce7', color: '#166534' },
      won: { bg: '#dcfce7', color: '#166534' },
      runner_up: { bg: '#fef3c7', color: '#92400e' },
      outbid: { bg: '#fee2e2', color: '#991b1b' },
      below_reserve: { bg: '#f3f4f6', color: '#6b7280' },
      forfeited: { bg: '#fee2e2', color: '#991b1b' },
      collected: { bg: '#d1fae5', color: '#065f46' },
    };
    return filteredBids.map((bid: any) => {
      const rc = resultColors[bid.result] || { bg: '#f3f4f6', color: '#6b7280' };
      return (
        <div key={bid.id} style={{ ...cardStyle, cursor: 'pointer' }} onClick={() => openAuctionDetail(bid.auction_item_id)}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
            {bid.image_id ? (
              <img src={`${API_BASE_URL}/api/images/${bid.image_id}/public`} alt={bid.item_name} style={thumbStyle} />
            ) : (
              <div style={thumbPlaceholder}>{(bid.item_name || '?').substring(0, 2).toUpperCase()}</div>
            )}
            <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
              <div style={{ fontWeight: 600, fontSize: '15px', color: 'var(--color-text-primary)', marginBottom: 2 }}>{bid.item_name}</div>
              <div style={{ fontSize: '13px', color: 'var(--color-text-muted)' }}>
                Your bid: ${centsToDisplay(bid.amount)}
                {bid.result === 'winner' && bid.collection_deadline && ` · Collect by ${formatDate(bid.collection_deadline)}`}
              </div>
            </div>
            <span style={{
              display: 'inline-block', padding: '3px 8px', borderRadius: 6,
              fontSize: '11px', fontWeight: 600, background: rc.bg, color: rc.color,
            }}>
              {(bid.result || 'pending').replace('_', ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())}
            </span>
          </div>
        </div>
      );
    });
  };

  const depotIsOwner = selectedDepot ? selectedDepot.owner_id === currentUserId : false;

  /* ============= MARKETPLACE-LEVEL ITEM SELECTION + CONTACT ============= */
  const [marketSelectedKeys, setMarketSelectedKeys] = useState<Set<string>>(new Set());
  const [showMarketContactModal, setShowMarketContactModal] = useState(false);
  const [showCopiedToast, setShowCopiedToast] = useState(false);

  // Reset marketplace selection when filter changes
  useEffect(() => {
    setMarketSelectedKeys(new Set());
    setShowMarketContactModal(false);
  }, [filter]);

  const getCardKey = (item: any) => {
    const isDepot = item._type === 'depot';
    return isDepot ? `depot-${item.depot_id}-${item.id}` : `${item._source}-${item.id}`;
  };

  const toggleMarketItemSelect = useCallback((item: any) => {
    const key = getCardKey(item);
    setMarketSelectedKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }, []);

  // Get selected items data for contact modal
  const marketSelectedItems = filteredItems.filter((item: any) => marketSelectedKeys.has(getCardKey(item)));
  // Contactable items: depot items + Buy Now auction items (pure auction items excluded)
  const marketContactableItems = marketSelectedItems.filter((item: any) => item._type === 'depot' || (item._hasBuyNow && item._depotItem));
  const firstSelectedSeller = (() => {
    for (const item of marketContactableItems) {
      if (item._type === 'depot') {
        return { sellerId: item.owner_id, sellerName: item.owner_name || 'Seller', collectionId: item.depot_id };
      }
      if (item._depotItem) {
        const d = item._depotItem;
        return { sellerId: d.owner_id, sellerName: d.owner_name || 'Seller', collectionId: d.depot_id };
      }
    }
    return null;
  })();

  /* ============= DEPOT ITEM SELECTION ============= */
  const [depotSelectedIds, setDepotSelectedIds] = useState<Set<number>>(new Set());
  const [showDepotContactModal, setShowDepotContactModal] = useState(false);

  // Reset selection when depot changes
  useEffect(() => {
    setDepotSelectedIds(new Set());
    setShowDepotContactModal(false);
  }, [selectedDepot?.depot_id]);

  const toggleDepotItemSelect = useCallback((item: any) => {
    setDepotSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(item.id)) next.delete(item.id); else next.add(item.id);
      return next;
    });
  }, []);

  /* ============= DEPOT ITEM EDIT MODAL ============= */
  const [editingDepotItem, setEditingDepotItem] = useState<EditableItem | null>(null);

  const handleDepotItemSave = useCallback(async () => {
    if (!editingDepotItem || !token) return;
    try {
      const resp = await fetch(API_ENDPOINTS.UPDATE_ITEMS, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{
          id: editingDepotItem.id,
          name: editingDepotItem.name,
          description: editingDepotItem.description,
          quantity: editingDepotItem.quantity,
          quantity_min: editingDepotItem.quantity_min,
          purchase_price: editingDepotItem.purchase_price,
          sale_price: (editingDepotItem as any).sale_price,
          condition: editingDepotItem.condition,
        }] }),
      });
      if (resp.ok) {
        // Refresh depot drill-down if open
        if (selectedDepot) openDepotDetail(selectedDepot);
        // Refresh All view data
        fetchAllDepotItems();
        fetchBrowse();
        fetchMyAuctions();
      }
    } catch { /* ignore */ }
    setEditingDepotItem(null);
  }, [editingDepotItem, token, selectedDepot, openDepotDetail, fetchAllDepotItems, fetchBrowse, fetchMyAuctions]);

  const filteredDepotItems = depotItems.filter((item: any) =>
    matchesSearch(item.name) || matchesSearch(item.description)
  );

  const renderDepotDrillDown = () => {
    if (!selectedDepot) return null;
    return (
      <>
        {/* Back arrow + depot name header */}
        <div style={{
          position: 'sticky', top: 0, zIndex: 50,
          padding: '10px 16px', textAlign: 'center',
          background: 'var(--color-bg-secondary)',
        }}>
          <button
            onClick={() => setSelectedDepot(null)}
            style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 4, display: 'flex', alignItems: 'center', color: 'var(--color-accent)' }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6" /></svg>
          </button>
          <div style={{ fontWeight: 700, fontSize: '16px', color: 'var(--color-text-primary)' }}>{selectedDepot.name}</div>
          <div style={{ fontSize: '12px', color: 'var(--color-text-muted)' }}>
            {selectedDepot.owner_name || 'Depot'} · {depotItems.length} item{depotItems.length !== 1 ? 's' : ''}
          </div>
          <div style={{ margin: '8px auto 0', width: '50%', borderBottom: '1px solid var(--color-border)' }} />
        </div>
        {selectedDepot.description && (
          <div style={{ padding: '8px 16px', fontSize: '13px', color: 'var(--color-text-muted)', textAlign: 'center', borderBottom: '1px solid var(--color-border)', background: 'var(--color-bg-secondary)' }}>
            {selectedDepot.description}
          </div>
        )}
        <InventoryContent
          items={filteredDepotItems}
          viewMode="marketplace-card"
          thumbnailSize={savedThumbSize as any}
          token={token}
          currentUserId={currentUserId || undefined}
          selectedIds={!depotIsOwner ? depotSelectedIds : new Set<number>()}
          isSelectMode={!depotIsOwner}
          isEditMode={false}
          loading={depotItemsLoading}
          highlightZeroQuantity={true}
          disableNameEdit={!depotIsOwner}
          hasActiveFilter={searchTerm.trim() !== ''}
          totalItemsBeforeFilter={depotItems.length}
          onCheckboxToggle={!depotIsOwner ? (item: any) => toggleDepotItemSelect(item) : undefined}
          onItemClick={!depotIsOwner ? (item: any) => toggleDepotItemSelect(item) : undefined}
          onMenuClick={depotIsOwner ? (item: any) => {
            setEditingDepotItem({
              id: item.id,
              name: item.name || '',
              description: item.description || '',
              color: '',
              area: '',
              location: '',
              quantity: item.quantity ?? 1,
              quantity_min: item.quantity_min,
              purchase_price: item.purchase_price,
              sale_price: item.sale_price,
              condition: item.condition,
              image_id: item.image_id,
            } as any);
          } : undefined}
        />

        {/* Floating bar for depot drill-down selection */}
        {!depotIsOwner && depotSelectedIds.size > 0 && (
          <div style={{
            position: 'fixed',
            bottom: 100,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            padding: '4px 6px',
            background: 'var(--color-bg-elevated)',
            backdropFilter: 'blur(20px) saturate(180%)',
            WebkitBackdropFilter: 'blur(20px) saturate(180%)',
            borderRadius: 20,
            boxShadow: '0 1px 8px var(--color-shadow), 0 0 1px var(--color-shadow-sm)',
            border: '0.5px solid var(--color-border-light)',
          }}>
            {/* Selected count */}
            <span style={{ padding: '5px 10px', fontSize: 13, fontWeight: 600, color: 'var(--color-accent-secondary)', whiteSpace: 'nowrap' }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: '-2px', marginRight: 4 }}>
                <polyline points="9 11 12 14 22 4"></polyline>
                <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"></path>
              </svg>
              {depotSelectedIds.size}
            </span>

            {/* Clear */}
            <div style={{ width: 1, height: 16, background: 'var(--color-border-strong)', margin: '0 2px' }} />
            <button
              onClick={() => setDepotSelectedIds(new Set())}
              style={{
                padding: '5px 10px', background: 'transparent', color: 'var(--color-text-secondary)',
                border: 'none', borderRadius: 14, fontSize: 13, fontWeight: 500, cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 4, transition: 'all 0.2s ease', whiteSpace: 'nowrap',
              }}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
              Clear
            </button>

            {/* Share link */}
            <div style={{ width: 1, height: 16, background: 'var(--color-border-strong)', margin: '0 2px' }} />
            <button
              onClick={() => {
                const itemIds = Array.from(depotSelectedIds).join(',');
                const base = window.location.origin + '/marketplace';
                const params = new URLSearchParams();
                if (selectedDepot.depot_id) params.set('depot', String(selectedDepot.depot_id));
                if (itemIds) params.set('item', itemIds);
                const url = params.toString() ? `${base}?${params}` : base;
                navigator.clipboard.writeText(url).then(() => {
                  setShowCopiedToast(true);
                  setTimeout(() => setShowCopiedToast(false), 2000);
                }).catch(() => { window.prompt('Copy this link:', url); });
              }}
              style={{
                padding: '5px 10px', background: 'transparent', color: 'var(--color-text-secondary)',
                border: 'none', borderRadius: 14, fontSize: 13, fontWeight: 500, cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 4, transition: 'all 0.2s ease', whiteSpace: 'nowrap',
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
                <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
              </svg>
              Share
            </button>

            {/* Contact Seller */}
            {selectedDepot.owner_id && (
              <>
                <div style={{ width: 1, height: 16, background: 'var(--color-border-strong)', margin: '0 2px' }} />
                <button
                  onClick={() => setShowDepotContactModal(true)}
                  style={{
                    padding: '5px 10px', background: 'var(--color-accent)', color: '#fff',
                    border: 'none', borderRadius: 14, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                    display: 'flex', alignItems: 'center', gap: 4, transition: 'all 0.2s ease', whiteSpace: 'nowrap',
                    boxShadow: '0 2px 8px rgba(0, 122, 255, 0.3)',
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="22" y1="2" x2="11" y2="13"></line>
                    <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                  </svg>
                  Contact
                </button>
              </>
            )}
          </div>
        )}

        {/* Contact Seller Modal for depot drill-down */}
        {showDepotContactModal && selectedDepot.owner_id && selectedDepot.depot_id && (
          <ContactSellerModal
            open={showDepotContactModal}
            onClose={() => setShowDepotContactModal(false)}
            sellerId={selectedDepot.owner_id}
            sellerName={selectedDepot.owner_name || 'Seller'}
            collectionId={selectedDepot.depot_id}
            reviewItems={depotItems
              .filter((item: any) => depotSelectedIds.has(item.id))
              .map((item: any) => ({
                id: item.id,
                name: item.name || '',
                qty: item.quantity ?? 1,
                maxQty: item.quantity ?? 1,
                price: (item.sale_price ?? 0) / 100,
                originalPrice: (item.sale_price ?? 0) / 100,
              }))}
          />
        )}
      </>
    );
  };

  const renderDepots = () => {
    // Drill-down: show items for selected depot
    if (selectedDepot) {
      return renderDepotDrillDown();
    }

    // Depot listing
    if (depotsLoading) return <div style={{ padding: 32, textAlign: 'center', color: 'var(--color-text-muted)' }}>Loading...</div>;
    if (filteredDepots.length === 0) return (
      <div style={{ padding: 32, textAlign: 'center', color: 'var(--color-text-muted)' }}>
        <div style={{ marginBottom: 8 }}>{searchLower ? 'No matching depots.' : 'No depots published yet.'}</div>
        {!searchLower && <div style={{ fontSize: '13px' }}>Create a marketplace collection in Inventory and publish it.</div>}
      </div>
    );
    return filteredDepots.map((depot: any) => (
      <div
        key={depot.depot_id}
        style={{ ...cardStyle, cursor: 'pointer' }}
        onClick={() => openDepotDetail(depot)}
      >
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
          {depot.preview_image ? (
            <img src={`${API_BASE_URL}${depot.preview_image}`} alt={depot.name} style={thumbStyle} />
          ) : (
            <div style={thumbPlaceholder}>{depot.icon || (depot.name || '?').substring(0, 2).toUpperCase()}</div>
          )}
          <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
            <div style={{ fontWeight: 600, fontSize: '15px', color: 'var(--color-text-primary)', marginBottom: 2 }}>{depot.name}</div>
            <div style={{ fontSize: '13px', color: 'var(--color-text-muted)' }}>
              {depot.item_count} item{depot.item_count !== 1 ? 's' : ''}
              {depot.description ? ` · ${depot.description}` : ''}
            </div>
          </div>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--color-text-muted)" strokeWidth="2"><polyline points="9 18 15 12 9 6" /></svg>
        </div>
      </div>
    ));
  };

  return (
    <>
      {isActive && (
        <UnifiedHeader
          title="Market"
          subtitle="Auctions & marketplace"
          showCollectionDropdown={false}
          showNotifications={false}
          showFontSizeToggle={!!onFontSizeToggle}
          onFontSizeToggle={onFontSizeToggle}
          fontSize={fontSize}
          onSockClick={toggleVoice}
          isVoiceListening={isListening}
          activeMode={activeMode}
          onOpenAppContext={onOpenAppContext}
          activeSpaceName={activeSpace?.name}
        />
      )}
      {/* Search bar + filter dropdown */}
      <div style={{ display: 'flex', gap: 8, padding: '8px 16px', background: 'var(--color-bg-primary)', borderBottom: '1px solid var(--color-border)', alignItems: 'center', position: 'sticky', top: 0, zIndex: 50 }}>
        {/* Search input */}
        <div style={{ flex: 1, position: 'relative', display: 'flex', alignItems: 'center' }}>
          <svg style={{ position: 'absolute', left: 10, color: 'var(--color-text-muted)', pointerEvents: 'none' }} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" /></svg>
          <input
            type="text"
            placeholder="Search items..."
            value={searchTerm}
            onChange={(e) => {
              if (isListening && recognitionRef.current) { try { recognitionRef.current.stop(); } catch {} }
              setSearchTerm(e.target.value);
            }}
            style={{ width: '100%', height: 38, padding: '0 36px 0 34px', border: 'none', borderRadius: 20, background: 'rgba(142,142,147,0.12)', color: 'var(--color-text-primary)', fontSize: 15, fontWeight: 400, outline: 'none' }}
          />
          <div style={{ position: 'absolute', right: 4, display: 'flex', alignItems: 'center' }}>
            {searchTerm ? (
              <button onClick={() => setSearchTerm('')} style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: 4, display: 'flex', alignItems: 'center', color: 'var(--color-text-muted)' }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><path d="M15 9l-6 6" /><path d="M9 9l6 6" /></svg>
              </button>
            ) : (
              <button onClick={toggleVoice} style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: 4, display: 'flex', alignItems: 'center', color: isListening ? 'var(--color-accent)' : 'var(--color-text-muted)' }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z" /><path d="M19 10v2a7 7 0 01-14 0v-2" /><line x1="12" y1="19" x2="12" y2="23" /><line x1="8" y1="23" x2="16" y2="23" /></svg>
              </button>
            )}
          </div>
        </div>
        {/* Proximity pill */}
        <button
          onClick={handleProximityButtonClick}
          style={{
            display: 'flex', alignItems: 'center', gap: 4, padding: '7px 12px', borderRadius: 20,
            fontSize: 13, fontWeight: maxDistance ? 600 : 400, whiteSpace: 'nowrap', flexShrink: 0,
            border: maxDistance ? '1.5px solid var(--color-accent)' : '1px solid var(--color-border)',
            background: maxDistance ? 'var(--color-accent-light, #e0f0ff)' : 'var(--color-bg-primary)',
            color: maxDistance ? 'var(--color-accent)' : 'var(--color-text-secondary)',
            cursor: 'pointer', transition: 'all 0.15s',
          }}
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
            <circle cx="12" cy="10" r="3"></circle>
          </svg>
          {maxDistance ? `${maxDistance} mi` : '? mi'}
        </button>
        {/* Filter dropdown trigger */}
        <div ref={filterDropdownRef} style={{ position: 'relative', flexShrink: 0 }}>
          <button
            onClick={() => setShowFilterDropdown(!showFilterDropdown)}
            style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '7px 12px', borderRadius: 20, fontSize: 13, fontWeight: 600, border: '1.5px solid var(--color-accent)', background: 'var(--color-bg-primary)', color: 'var(--color-accent)', cursor: 'pointer', whiteSpace: 'nowrap' }}
          >
            {FILTER_LABELS[filter]}
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ transform: showFilterDropdown ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.2s' }}><polyline points="6 9 12 15 18 9" /></svg>
          </button>
          {showFilterDropdown && (
            <div style={{ position: 'absolute', top: '100%', right: 0, marginTop: 4, background: 'var(--color-bg-primary)', border: '1px solid var(--color-border)', borderRadius: 12, boxShadow: '0 4px 20px rgba(0,0,0,0.15)', zIndex: 100, minWidth: 160, overflow: 'hidden' }}>
              {(Object.keys(FILTER_LABELS) as Filter[]).map((key) => (
                <button
                  key={key}
                  onClick={() => { setFilter(key); setShowFilterDropdown(false); setSelectedDepot(null); }}
                  style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '10px 14px', border: 'none', background: filter === key ? 'rgba(142,142,147,0.12)' : 'transparent', color: filter === key ? 'var(--color-accent)' : 'var(--color-text-primary)', fontSize: 14, fontWeight: filter === key ? 600 : 400, cursor: 'pointer', textAlign: 'left' }}
                >
                  {filter === key && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12" /></svg>}
                  <span style={{ marginLeft: filter === key ? 0 : 22 }}>{FILTER_LABELS[key]}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
      <div style={{ paddingBottom: 100 }}>
        {selectedDepot ? renderDepotDrillDown() : filter === 'my-depots' ? renderDepots() : filter === 'my-bids' ? renderBids() : renderItems()}
      </div>

      {/* Floating action bar — shown when non-owner items are selected */}
      {!selectedDepot && filter !== 'my-bids' && marketSelectedKeys.size > 0 && (
        <div style={{
          position: 'fixed',
          bottom: 100,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          padding: '4px 6px',
          background: 'var(--color-bg-elevated)',
          backdropFilter: 'blur(20px) saturate(180%)',
          WebkitBackdropFilter: 'blur(20px) saturate(180%)',
          borderRadius: 20,
          boxShadow: '0 1px 8px var(--color-shadow), 0 0 1px var(--color-shadow-sm)',
          border: '0.5px solid var(--color-border-light)',
        }}>
          {/* Selected count */}
          <span style={{
            padding: '5px 10px',
            fontSize: 13,
            fontWeight: 600,
            color: 'var(--color-accent-secondary)',
            whiteSpace: 'nowrap',
          }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: '-2px', marginRight: 4 }}>
              <polyline points="9 11 12 14 22 4"></polyline>
              <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"></path>
            </svg>
            {marketSelectedKeys.size}
          </span>

          {/* Clear */}
          <div style={{ width: 1, height: 16, background: 'var(--color-border-strong)', margin: '0 2px' }} />
          <button
            onClick={() => setMarketSelectedKeys(new Set())}
            style={{
              padding: '5px 10px',
              background: 'transparent',
              color: 'var(--color-text-secondary)',
              border: 'none',
              borderRadius: 14,
              fontSize: 13,
              fontWeight: 500,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              transition: 'all 0.2s ease',
              whiteSpace: 'nowrap',
            }}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
            Clear
          </button>

          {/* Share link — copies public marketplace URL */}
          <div style={{ width: 1, height: 16, background: 'var(--color-border-strong)', margin: '0 2px' }} />
          <button
            onClick={() => {
              const base = window.location.origin + '/marketplace';
              const params = new URLSearchParams();
              // Collect depot and item IDs from selected items
              const depotIds = new Set<string>();
              const itemIds: string[] = [];
              for (const item of marketSelectedItems) {
                if (item._type === 'depot') {
                  if (item.depot_id) depotIds.add(String(item.depot_id));
                  itemIds.push(String(item.id));
                } else {
                  itemIds.push(String(item.item_id || item.id));
                }
              }
              if (depotIds.size === 1) params.set('depot', Array.from(depotIds)[0]);
              if (itemIds.length > 0) params.set('item', itemIds.join(','));
              const url = params.toString() ? `${base}?${params}` : base;
              navigator.clipboard.writeText(url).then(() => {
                setShowCopiedToast(true);
                setTimeout(() => setShowCopiedToast(false), 2000);
              }).catch(() => { window.prompt('Copy this link:', url); });
            }}
            style={{
              padding: '5px 10px',
              background: 'transparent',
              color: 'var(--color-text-secondary)',
              border: 'none',
              borderRadius: 14,
              fontSize: 13,
              fontWeight: 500,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              transition: 'all 0.2s ease',
              whiteSpace: 'nowrap',
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
              <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
            </svg>
            Share
          </button>

          {/* Contact Seller */}
          {firstSelectedSeller && (
            <>
              <div style={{ width: 1, height: 16, background: 'var(--color-border-strong)', margin: '0 2px' }} />
              <button
                onClick={() => setShowMarketContactModal(true)}
                style={{
                  padding: '5px 10px',
                  background: 'var(--color-accent)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 14,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                  transition: 'all 0.2s ease',
                  whiteSpace: 'nowrap',
                  boxShadow: '0 2px 8px rgba(0, 122, 255, 0.3)',
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="22" y1="2" x2="11" y2="13"></line>
                  <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                </svg>
                Contact
              </button>
            </>
          )}
        </div>
      )}

      {/* Contact Seller Modal — marketplace-level */}
      {showMarketContactModal && firstSelectedSeller && (
        <ContactSellerModal
          open={showMarketContactModal}
          onClose={() => setShowMarketContactModal(false)}
          sellerId={firstSelectedSeller.sellerId}
          sellerName={firstSelectedSeller.sellerName}
          collectionId={firstSelectedSeller.collectionId}
          reviewItems={marketContactableItems.map((item: any) => {
            if (item._type === 'depot') {
              return {
                id: item.id,
                name: item.name || '',
                qty: item.quantity ?? 1,
                maxQty: item.quantity ?? 1,
                price: (item.sale_price ?? 0) / 100,
                originalPrice: (item.sale_price ?? 0) / 100,
              };
            }
            // Buy Now auction item — use depot data with locked buy_now_price
            const d = item._depotItem;
            const bnPrice = (item.buy_now_price ?? 0) / 100;
            return {
              id: d.id,
              name: d.name || item.item_name || '',
              qty: d.quantity ?? 1,
              maxQty: d.quantity ?? 1,
              price: bnPrice,
              originalPrice: bnPrice,
              priceLocked: true,
            };
          })}
        />
      )}

      {/* Copied toast */}
      {showCopiedToast && (
        <div style={{
          position: 'fixed', bottom: 160, left: '50%', transform: 'translateX(-50%)', zIndex: 2000,
          background: 'var(--color-text-primary)', color: 'var(--color-bg-primary)',
          padding: '10px 20px', borderRadius: '8px', fontSize: 13, fontWeight: 500,
          boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
        }}>
          Link copied!
        </div>
      )}

      {/* Auction Detail Bottom Sheet */}
      <BottomSheet
        isOpen={detailAuctionId !== null}
        onClose={closeAuctionDetail}
        title="Auction"
        style={{ maxHeight: 'calc(100vh - env(safe-area-inset-top, 44px) - 16px)' }}
      >
        {detailLoading ? (
          <div style={{ padding: 32, textAlign: 'center', color: 'var(--color-text-muted)' }}>Loading...</div>
        ) : detailError ? (
          <div style={{ padding: 32, textAlign: 'center', color: 'var(--color-text-muted)' }}>{detailError}</div>
        ) : detailAuction ? (
          <div>
            {/* Item image */}
            {detailAuction.image_id ? (
              <div style={{ position: 'relative', marginBottom: 16 }}>
                <img
                  src={`${API_BASE_URL}/api/images/${detailAuction.image_id}/public`}
                  alt={detailAuction.item_name}
                  style={{ width: '100%', borderRadius: 12, maxHeight: 300, objectFit: 'cover' }}
                />
                <div style={{
                  position: 'absolute', top: 12, left: 12,
                  padding: '4px 10px', borderRadius: 6, fontSize: 12, fontWeight: 700,
                  background: detailAuction.status === 'active' ? '#22c55e' : '#6b7280',
                  color: '#fff',
                }}>
                  {detailAuction.status === 'active' ? detailTimeRemaining : detailAuction.status.charAt(0).toUpperCase() + detailAuction.status.slice(1)}
                </div>
              </div>
            ) : null}

            {/* Title + description */}
            <h2 style={{ fontSize: 20, fontWeight: 700, color: 'var(--color-text-primary)', margin: '0 0 4px' }}>{detailAuction.item_name}</h2>
            {detailAuction.item_description && (
              <p style={{ fontSize: 14, color: 'var(--color-text-muted)', margin: '0 0 12px' }}>{detailAuction.item_description}</p>
            )}

            {/* Pricing line */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 16px', fontSize: 13, color: 'var(--color-text-secondary)', marginBottom: 8 }}>
              <span>Min <strong style={{ color: 'var(--color-text-primary)' }}>${centsToDisplay(detailAuction.minimum_bid)}</strong></span>
              {detailAuction.reserve_visible && detailAuction.reserve_price && (
                <span>Reserve <strong style={{ color: 'var(--color-text-primary)' }}>${centsToDisplay(detailAuction.reserve_price)}</strong></span>
              )}
              {detailAuction.buy_now_price && (
                <span>Buy Now <strong style={{ color: '#16a34a' }}>${centsToDisplay(detailAuction.buy_now_price)}</strong></span>
              )}
              <span>{detailAuction.bid_count} bid{detailAuction.bid_count !== 1 ? 's' : ''}</span>
            </div>

            {/* Seller info */}
            <div style={{ fontSize: 14, color: 'var(--color-text-secondary)', marginBottom: 12, paddingTop: 8, borderTop: '1px solid var(--color-border)' }}>
              Seller: {detailAuction.seller_name}
              {detailAuction.seller_location && <span style={{ marginLeft: 8 }}>{detailAuction.seller_location}</span>}
            </div>

            {detailAuction.collection_notes && (
              <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginBottom: 12 }}>
                <strong>Pickup:</strong> {detailAuction.collection_notes}
              </div>
            )}

            {/* Outcome banners */}
            {detailAuction.status === 'closed' && (
              <div style={{ padding: '10px 14px', borderRadius: 8, background: '#f3f4f6', color: '#374151', fontSize: 14, fontWeight: 600, marginBottom: 12 }}>
                Auction ended — {detailAuction.outcome === 'sold' ? 'Sold' : detailAuction.outcome === 'unsold' ? 'Unsold' : 'Awaiting collection'}
              </div>
            )}
            {detailAuction.status === 'completed' && (
              <div style={{ padding: '10px 14px', borderRadius: 8, background: '#dcfce7', color: '#166534', fontSize: 14, fontWeight: 600, marginBottom: 12 }}>
                {detailAuction.outcome === 'sold' ? `Sold for $${centsToDisplay(detailAuction.winning_bid || 0)}` : 'Auction completed — unsold'}
              </div>
            )}
            {detailAuction.status === 'cancelled' && (
              <div style={{ padding: '10px 14px', borderRadius: 8, background: '#fee2e2', color: '#991b1b', fontSize: 14, fontWeight: 600, marginBottom: 12 }}>
                Seller withdrew this item from auction
              </div>
            )}

            {/* Compact bid + Buy It Now section */}
            {detailAuction.status === 'active' && !detailAuction.is_seller && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {/* Bid row: [Place/Update Bid] [$ input] [Withdraw?] */}
                <div style={{ display: 'flex', alignItems: 'stretch', gap: 6 }}>
                  <button
                    onTouchEnd={(e) => { e.preventDefault(); handlePlaceBid(); }}
                    onClick={handlePlaceBid}
                    disabled={bidSubmitting}
                    style={{
                      flex: 1, padding: '10px 0', fontSize: 14, fontWeight: 600, borderRadius: 8,
                      border: 'none', background: '#3B82F6', color: '#fff', cursor: 'pointer',
                      opacity: bidSubmitting ? 0.6 : 1, whiteSpace: 'nowrap',
                    }}
                  >
                    {bidSubmitting ? '...' : detailAuction.my_bid ? 'Update Bid' : 'Place Bid'}
                  </button>
                  <div style={{ flex: 1, display: 'flex', alignItems: 'stretch', gap: 0, minWidth: 0 }}>
                    <span style={{
                      fontSize: 14, fontWeight: 600, color: 'var(--color-text-muted)',
                      padding: '0 6px 0 10px', background: 'var(--color-bg-secondary)',
                      border: '1px solid var(--color-border)', borderRight: 'none',
                      borderRadius: '8px 0 0 8px',
                      display: 'flex', alignItems: 'center',
                    }}>$</span>
                    <input
                      type="number"
                      value={bidAmount}
                      onChange={(e) => setBidAmount(e.target.value)}
                      ref={bidInputRef}
                      onFocus={() => {
                        setBidInputFocused(true);
                        // After spacer renders + keyboard opens, scroll input into view within the bottom sheet
                        setTimeout(() => {
                          bidInputRef.current?.closest('.bottom-sheet-content')?.scrollTo({
                            top: (bidInputRef.current?.closest('.bottom-sheet-content')?.scrollHeight ?? 0),
                            behavior: 'smooth',
                          });
                        }, 150);
                      }}
                      onBlur={() => { setTimeout(() => setBidInputFocused(false), 200); }}
                      min={(detailAuction.minimum_bid / 100).toFixed(2)}
                      step="0.01"
                      disabled={bidSubmitting}
                      placeholder={detailAuction.my_bid ? centsToDisplay(detailAuction.my_bid.amount) : (detailAuction.minimum_bid / 100).toFixed(2)}
                      style={{
                        flex: 1, minWidth: 0, padding: '10px 10px', fontSize: 14, borderRadius: '0 8px 8px 0',
                        border: '1px solid var(--color-border)', borderLeft: 'none',
                        background: 'var(--color-bg-secondary)', color: 'var(--color-text-primary)',
                      }}
                    />
                  </div>
                  {detailAuction.my_bid && (
                    <button
                      onTouchEnd={(e) => { e.preventDefault(); handleWithdrawBid(); }}
                      onClick={handleWithdrawBid}
                      disabled={bidSubmitting}
                      style={{
                        flex: 1, padding: '10px 0', fontSize: 14, fontWeight: 600, borderRadius: 8,
                        border: '1px solid var(--color-border)', background: 'var(--color-bg-secondary)',
                        color: 'var(--color-text-secondary)', cursor: 'pointer', whiteSpace: 'nowrap',
                      }}
                    >
                      Withdraw
                    </button>
                  )}
                </div>
                {/* Buy It Now row */}
                {detailAuction.buy_now_price && (
                  <button
                    onClick={handleBuyNow}
                    style={{
                      width: '100%', padding: '10px', fontSize: 14, fontWeight: 600, borderRadius: 8,
                      border: 'none', background: '#16a34a', color: '#fff', cursor: 'pointer',
                    }}
                  >
                    Buy It Now — ${centsToDisplay(detailAuction.buy_now_price)}
                  </button>
                )}
              </div>
            )}

            {/* Seller actions */}
            {detailAuction.is_seller && (
              <div style={{ paddingTop: 12, borderTop: '1px solid var(--color-border)' }}>
                <h3 style={{ fontSize: 16, fontWeight: 700, color: 'var(--color-text-primary)', margin: '0 0 10px' }}>Manage Auction</h3>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
                  {(detailAuction.status === 'draft' || detailAuction.status === 'scheduled' || detailAuction.status === 'active') && (
                    <button
                      onClick={() => { setEditAuctionData(detailAuction); closeAuctionDetail(); }}
                      style={sellerActionBtnStyle('#2563eb')}
                    >Edit</button>
                  )}
                  {detailAuction.status === 'active' && (
                    <>
                      <button onClick={handleSellerClose} disabled={sellerActionLoading} style={sellerActionBtnStyle('#6b7280')}>Close Early</button>
                      <button onClick={handleSellerWithdraw} disabled={sellerActionLoading} style={sellerActionBtnStyle('#dc2626')}>Withdraw</button>
                    </>
                  )}
                  {detailAuction.status === 'scheduled' && (
                    <button onClick={handleSellerWithdraw} disabled={sellerActionLoading} style={sellerActionBtnStyle('#dc2626')}>Cancel</button>
                  )}
                  {detailAuction.status === 'draft' && (
                    <button onClick={handleSellerPublish} disabled={sellerActionLoading} style={sellerActionBtnStyle('#059669')}>Publish</button>
                  )}
                  {detailAuction.status === 'closed' && detailAuction.outcome === 'sold' && (
                    <>
                      <button onClick={handleSellerCollect} disabled={sellerActionLoading} style={sellerActionBtnStyle('#059669')}>Mark Collected</button>
                      <button onClick={handleSellerForfeit} disabled={sellerActionLoading} style={sellerActionBtnStyle('#d97706')}>Winner Forfeit</button>
                    </>
                  )}
                </div>

                {/* Bid rankings for closed/completed */}
                {(detailAuction.status === 'closed' || detailAuction.status === 'completed') && (
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--color-text-primary)', marginBottom: 8 }}>Bid Rankings</div>
                    {rankingsLoading ? (
                      <div style={{ fontSize: 13, color: 'var(--color-text-muted)', padding: '8px 0' }}>Loading...</div>
                    ) : rankings.length === 0 ? (
                      <div style={{ fontSize: 13, color: 'var(--color-text-muted)', padding: '8px 0' }}>No bids received.</div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {rankings.map((r: any) => (
                          <div key={r.rank} style={{
                            display: 'flex', alignItems: 'flex-start', gap: 10,
                            padding: '8px 10px', borderRadius: 8,
                            background: r.result === 'winner' ? '#ecfdf5' : r.result === 'forfeited' ? '#fef2f2' : 'var(--color-bg-secondary)',
                            border: r.result === 'winner' ? '1px solid #a7f3d0' : '1px solid var(--color-border)',
                          }}>
                            <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--color-text-muted)', minWidth: 20 }}>#{r.rank}</div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--color-text-primary)' }}>
                                {r.bidder_name}
                                {r.result === 'winner' && <span style={{ color: '#059669', marginLeft: 6, fontSize: 12 }}>WINNER</span>}
                                {r.result === 'forfeited' && <span style={{ color: '#dc2626', marginLeft: 6, fontSize: 12 }}>FORFEITED</span>}
                                {r.result === 'runner_up' && <span style={{ color: '#6b7280', marginLeft: 6, fontSize: 12 }}>RUNNER-UP</span>}
                              </div>
                              <div style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>${centsToDisplay(r.amount)}</div>
                              {r.comment && (
                                <div style={{ fontSize: 13, color: 'var(--color-text-muted)', marginTop: 4, fontStyle: 'italic' }}>"{r.comment}"</div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Bid message */}
            {bidMessage && (
              <div style={{
                marginTop: 12, padding: '10px 14px', borderRadius: 8, fontSize: 14, fontWeight: 500,
                background: bidMessage.type === 'success' ? '#dcfce7' : '#fee2e2',
                color: bidMessage.type === 'success' ? '#166534' : '#991b1b',
              }}>
                {bidMessage.text}
              </div>
            )}

            {/* Keyboard spacer — pushes content up so bid input is visible above iOS keyboard */}
            {bidInputFocused && <div style={{ height: Math.max(bidKeyboardHeight, 300), flexShrink: 0 }} />}
          </div>
        ) : null}
      </BottomSheet>

      {/* Edit Auction Modal (reuses existing AuctionSetupModal) */}
      {editAuctionData && token && (
        <AuctionSetupModal
          itemId={editAuctionData.item_id}
          itemName={editAuctionData.item_name}
          token={token}
          onClose={() => setEditAuctionData(null)}
          onCreated={() => {
            setEditAuctionData(null);
            setBidMessage({ type: 'success', text: 'Auction updated.' });
            fetchAuctionDetail();
            fetchMyAuctions();
          }}
          editData={{
            id: editAuctionData.id,
            status: editAuctionData.status,
            starts_at: editAuctionData.starts_at,
            ends_at: editAuctionData.ends_at,
            minimum_bid: editAuctionData.minimum_bid,
            reserve_price: editAuctionData.reserve_price,
            reserve_visible: editAuctionData.reserve_visible,
            buy_now_price: editAuctionData.buy_now_price,
            quantity: editAuctionData.quantity,
            collection_window_days: editAuctionData.collection_window_days,
            collection_notes: editAuctionData.collection_notes,
            auction_group: editAuctionData.auction_group,
          }}
        />
      )}

      {/* Image Attribution Modal */}
      {imageModalItem && (
        <ImageAttributionModal
          isOpen={true}
          onClose={() => setImageModalItem(null)}
          imageUrl={`${API_ENDPOINTS.GET_IMAGE_CONTENT(imageModalItem.image_id)}`}
          itemName={imageModalItem.name}
          token={token || undefined}
          onChangeImage={imageModalItem.isMine ? () => {
            const { itemId: imgItemId, isDepot: imgIsDepot, depotId: imgDepotId } = imageModalItem;
            setImageModalItem(null);
            // Find the exact item and open edit modal
            const found = filteredItems.find((it: any) =>
              imgIsDepot
                ? (it._type === 'depot' && it.id === imgItemId && it.depot_id === imgDepotId)
                : (it._type !== 'depot' && it.item_id === imgItemId)
            );
            if (found) {
              // For auction items, cross-reference depot data for fields the auction API doesn't return
              const imgDepot = !imgIsDepot ? depotByItemId.get(found.item_id) : null;
              setEditingDepotItem({
                id: imgItemId,
                name: imgIsDepot ? (found.name || '') : (found.item_name || found.name || ''),
                description: imgIsDepot ? (found.description || '') : (found.item_description || ''),
                color: '',
                area: '',
                location: '',
                quantity: found.quantity ?? imgDepot?.quantity ?? 1,
                quantity_min: found.quantity_min ?? imgDepot?.quantity_min,
                purchase_price: found.purchase_price ?? imgDepot?.purchase_price,
                sale_price: found.sale_price ?? imgDepot?.sale_price,
                condition: imgIsDepot ? found.condition : (found.item_condition || found.condition),
                image_id: found.image_id,
              } as any);
            }
          } : undefined}
        />
      )}

      {/* Depot Item Edit Modal */}
      {editingDepotItem && (
        <UnifiedItemEditModal
          open={true}
          item={editingDepotItem}
          onClose={() => setEditingDepotItem(null)}
          onChange={(patch) => setEditingDepotItem(prev => prev ? { ...prev, ...patch } : null)}
          onSave={handleDepotItemSave}
          mode="full"
          token={token || undefined}
        />
      )}

      {/* Proximity help tip */}
      <ContextHelpTip tipId="market-proximity" show={!!maxDistance} position="bottom" bottomOffset={80} />

      {/* Proximity / Location Modal */}
      {showLocationModal && (
        <div
          style={{ position: 'fixed', inset: 0, zIndex: 2000, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
          onClick={() => { setShowLocationModal(false); setLocationError(null); }}
        >
          <div
            style={{ background: 'var(--color-bg-primary)', borderRadius: 16, padding: 24, maxWidth: 380, width: '100%', boxShadow: '0 10px 40px rgba(0,0,0,0.15)' }}
            onClick={(e) => e.stopPropagation()}
          >
            {!viewerLoc ? (
              <>
                <div style={{ textAlign: 'center', marginBottom: 16 }}>
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="var(--color-accent)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: 8 }}>
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                    <circle cx="12" cy="10" r="3"></circle>
                  </svg>
                  <div style={{ fontSize: 'var(--font-size-lg)', fontWeight: 700, color: 'var(--color-text-primary)' }}>Set Your Location</div>
                  <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)', marginTop: 4 }}>To filter items by distance</div>
                </div>
                <button
                  onClick={handleBrowserLocation}
                  disabled={locatingBrowser}
                  style={{ width: '100%', padding: 12, marginBottom: 10, borderRadius: 10, border: 'none', background: 'var(--color-accent)', color: '#fff', fontSize: 'var(--font-size-sm)', fontWeight: 600, cursor: locatingBrowser ? 'wait' : 'pointer', opacity: locatingBrowser ? 0.7 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"></circle><line x1="12" y1="2" x2="12" y2="6"></line><line x1="12" y1="18" x2="12" y2="22"></line><line x1="2" y1="12" x2="6" y2="12"></line><line x1="18" y1="12" x2="22" y2="12"></line>
                  </svg>
                  {locatingBrowser ? 'Locating...' : 'Use My Location'}
                </button>
                <div style={{ textAlign: 'center', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', margin: '6px 0' }}>or</div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input
                    type="text" inputMode="numeric" placeholder="Zip code" maxLength={5}
                    value={customDistanceInput}
                    onChange={(e) => setCustomDistanceInput(e.target.value.replace(/\D/g, ''))}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleZipLookup(customDistanceInput); }}
                    style={{ flex: 1, padding: '10px 12px', borderRadius: 10, border: '1px solid var(--color-border)', background: 'var(--color-bg-secondary, var(--color-bg-primary))', color: 'var(--color-text-primary)', fontSize: 'var(--font-size-sm)', outline: 'none' }}
                  />
                  <button
                    onClick={() => handleZipLookup(customDistanceInput)}
                    disabled={locatingBrowser || customDistanceInput.length < 5}
                    style={{ padding: '10px 16px', borderRadius: 10, border: 'none', background: 'var(--color-accent)', color: '#fff', fontSize: 'var(--font-size-sm)', fontWeight: 600, cursor: 'pointer', opacity: customDistanceInput.length < 5 ? 0.5 : 1 }}
                  >Go</button>
                </div>
                {locationError && (
                  <div style={{ marginTop: 10, fontSize: 'var(--font-size-xs)', color: '#dc2626', textAlign: 'center' }}>{locationError}</div>
                )}
              </>
            ) : (
              <>
                <div style={{ textAlign: 'center', marginBottom: 16 }}>
                  <div style={{ fontSize: 'var(--font-size-lg)', fontWeight: 700, color: 'var(--color-text-primary)' }}>Search Radius</div>
                  <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', marginTop: 4 }}>Show items within...</div>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center', marginBottom: 12 }}>
                  {DISTANCE_PRESETS.map((d) => (
                    <button
                      key={d}
                      onClick={() => selectDistance(d)}
                      style={{ padding: '8px 18px', borderRadius: 20, fontSize: 'var(--font-size-sm)', fontWeight: maxDistance === d ? 700 : 500, border: maxDistance === d ? '2px solid var(--color-accent)' : '1px solid var(--color-border)', background: maxDistance === d ? 'var(--color-accent-light, #e0f0ff)' : 'var(--color-bg-primary)', color: maxDistance === d ? 'var(--color-accent)' : 'var(--color-text-primary)', cursor: 'pointer', transition: 'all 0.15s' }}
                    >{d} mi</button>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12 }}>
                  <input
                    type="text" inputMode="numeric" placeholder="Custom miles"
                    value={customDistanceInput}
                    onChange={(e) => setCustomDistanceInput(e.target.value.replace(/\D/g, ''))}
                    onKeyDown={(e) => { if (e.key === 'Enter' && customDistanceInput) selectDistance(Number(customDistanceInput)); }}
                    style={{ flex: 1, padding: '10px 12px', borderRadius: 10, border: '1px solid var(--color-border)', background: 'var(--color-bg-secondary, var(--color-bg-primary))', color: 'var(--color-text-primary)', fontSize: 'var(--font-size-sm)', outline: 'none' }}
                  />
                  <button
                    onClick={() => { if (customDistanceInput) selectDistance(Number(customDistanceInput)); }}
                    disabled={!customDistanceInput}
                    style={{ padding: '10px 16px', borderRadius: 10, border: 'none', background: 'var(--color-accent)', color: '#fff', fontSize: 'var(--font-size-sm)', fontWeight: 600, cursor: 'pointer', opacity: customDistanceInput ? 1 : 0.5 }}
                  >Set</button>
                </div>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                  <button
                    onClick={() => { setShowLocationModal(false); setCustomDistanceInput(''); }}
                    style={{ padding: '8px 16px', borderRadius: 10, border: '1px solid var(--color-border)', background: 'var(--color-bg-primary)', color: 'var(--color-text-secondary)', cursor: 'pointer', fontSize: 'var(--font-size-xs)' }}
                  >Cancel</button>
                  <button
                    onClick={() => { clearProximity(); setCustomDistanceInput(''); }}
                    style={{ padding: '8px 16px', borderRadius: 10, border: '1px solid var(--color-border)', background: 'var(--color-bg-primary)', color: 'var(--color-text-muted)', cursor: 'pointer', fontSize: 'var(--font-size-xs)' }}
                  >Change Location</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default MarketTab;
